import React, { createContext, Dispatch, SetStateAction, useCallback, useMemo, useState, useContext } from 'react';

import { Exclusion } from '../../api/types/Exclusions';
import { omitFields } from '../../../../core/utils/utils';

export const TEMP_ID_PREFIX = 'temp-';

interface ExclusionDrawerContextProps {
    createExclusion: (exclusion: Exclusion) => void;
    updateExclusion: (id: string, exclusion: Partial<Exclusion>) => void;
    deleteExclusion: (id: string, name: string) => void;
    getPendingChanges: () => {
        createdExclusions: Exclusion[];
        updatedExclusions: Partial<Exclusion>[];
        deletedExclusionIds: string[];
    };
    clearPendingChanges: () => void;
    selectedExclusion?: Exclusion;
    setSelectedExclusion: Dispatch<SetStateAction<Exclusion | undefined>>;
    exclusions: Exclusion[];
    hasPendingChanges: boolean;
    exclusionsLoading?: boolean;
}

const DEFAULT_CONTEXT_VALUES: ExclusionDrawerContextProps = {
    createExclusion: async () => {},
    updateExclusion: async () => {},
    deleteExclusion: async () => {},
    getPendingChanges: () => ({ createdExclusions: [], updatedExclusions: [], deletedExclusionIds: [] }),
    clearPendingChanges: () => {},
    setSelectedExclusion: () => {},
    exclusionsLoading: false,
    exclusions: [],
    hasPendingChanges: false
};

const IncidentExclusionContext = createContext<ExclusionDrawerContextProps>(DEFAULT_CONTEXT_VALUES);
export default IncidentExclusionContext;

interface IncidentExclusionProviderProps {
    children: React.ReactNode;
    exclusions: Exclusion[];
    exclusionsLoading: boolean;
}

export const IncidentExclusionProvider: React.FC<IncidentExclusionProviderProps> = ({
    children,
    exclusions,
    exclusionsLoading
}) => {
    const [selectedExclusion, setSelectedExclusion] = useState<Exclusion | undefined>(undefined);
    const [pendingCreates, setPendingCreates] = useState<Map<string, Exclusion>>(new Map());
    const [pendingUpdates, setPendingUpdates] = useState<Map<string, Partial<Exclusion>>>(new Map());
    const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());

    const createExclusion = useCallback((exclusionReq: Exclusion) => {
        setPendingCreates((prev) => new Map(prev).set(exclusionReq.id, exclusionReq));
    }, []);

    const updateExclusion = useCallback(
        (id: string, updates: Partial<Exclusion>) => {
            if (pendingDeletes.has(id)) return;

            if (pendingCreates.has(id)) {
                setPendingCreates((prev) => {
                    const next = new Map(prev);
                    const existing = next.get(id);
                    if (existing) next.set(id, { ...existing, ...updates });
                    return next;
                });
                return;
            }

            setPendingUpdates((prev) => {
                const next = new Map(prev);
                const existingUpdates = next.get(id) || {};
                next.set(id, { ...existingUpdates, ...updates });
                return next;
            });
        },
        [pendingCreates, pendingDeletes]
    );

    const deleteExclusion = useCallback(
        (id: string) => {
            if (pendingCreates.has(id)) {
                setPendingCreates((prev) => {
                    const next = new Map(prev);
                    next.delete(id);
                    return next;
                });
                return;
            }

            setPendingUpdates((prev) => {
                if (!prev.has(id)) return prev;
                const next = new Map(prev);
                next.delete(id);
                return next;
            });

            setPendingDeletes((prev) => new Set([...prev, id]));
            setSelectedExclusion(undefined);
        },
        [pendingCreates]
    );

    const getPendingChanges = useCallback(() => {
        const createdExclusions: Exclusion[] = [...pendingCreates.values()].map(
            (exclusion) => omitFields(exclusion, ['id', 'excluded_entities']) as Exclusion
        );

        const updatedExclusions: Partial<Exclusion>[] = [...pendingUpdates.values()].map(
            (exclusion) => omitFields(exclusion, ['excluded_entities']) as Partial<Exclusion>
        );
        const deletedExclusionIds: string[] = Array.from(pendingDeletes.values());
        return { createdExclusions, updatedExclusions, deletedExclusionIds };
    }, [pendingCreates, pendingUpdates, pendingDeletes]);

    const combinedExclusions = useMemo(() => {
        const combinedData: Exclusion[] = [];
        exclusions.forEach((e) => {
            if (!pendingDeletes.has(e.id)) {
                const update = pendingUpdates.get(e.id);
                combinedData.push(update ? { ...e, ...update } : e);
            }
        });
        pendingCreates.forEach((excl, id) => {
            combinedData.push(excl);
        });
        return combinedData;
    }, [exclusions, pendingCreates, pendingUpdates, pendingDeletes]);

    const hasPendingChanges = pendingCreates.size > 0 || pendingUpdates.size > 0 || pendingDeletes.size > 0;

    const clearPendingChanges = useCallback(() => {
        setPendingCreates(new Map());
        setPendingUpdates(new Map());
        setPendingDeletes(new Set());
    }, []);

    return (
        <IncidentExclusionContext.Provider
            value={{
                createExclusion,
                updateExclusion,
                deleteExclusion,
                getPendingChanges,
                clearPendingChanges,
                selectedExclusion,
                setSelectedExclusion,
                exclusions: combinedExclusions,
                hasPendingChanges,
                exclusionsLoading: exclusionsLoading
            }}>
            {children}
        </IncidentExclusionContext.Provider>
    );
};

export const useIncidentExclusionContext = () => useContext(IncidentExclusionContext);
